package com.neurobs.ccab.casr

import com.neurobs.ccab.db.TestData
import com.neurobs.util.Guid
import scala.collection.mutable

/** Data for an entry in the test filter list box
 *
 * @param testId data.test.id database field
 * @param testName data.test.display_name database field
 * @param testIdentifier data.test.identifier database field
 */
case class TestChoice
(
	testId: Long,
	testName: String,
	testIdentifier: String
)

object TestChoice {
	val nullChoice = TestChoice(0, "", "")
}

/** Data for an entry in the subject filter list box
 *
 * @param accountSubjectId data.account_subject.id database field
 * @param reportingId data.account_subject.reporting_id database field
 */
case class AccountSubjectChoice
(
	accountSubjectId: Long,
	reportingId: Guid
)

object AccountSubjectChoice {
	val nullChoice = AccountSubjectChoice(0, Guid.nullGuid)
}

/** Item in a left join of successful test transcripts and reviewed transcripts
 *
 * These items are returned in the DB query for all test transcripts in a study.
 *
 * @param testTranscriptId casr.test_transcript.id database field
 * @param reviewedTestTranscriptId casr.reviewed_test_transcript.id database field
 * @param accountSubjectId data.account_subject.id database field
 * @param reportingId data.account_subject.reporting_id database field
 * @param testId data.test.id database field
 */
case class TranscriptReviewInstance
(
	testTranscriptId: Long,
	reviewedTestTranscriptId: Option[Long],
	accountSubjectId: Long,
	reportingId: Guid,
	testId: Long
)

/** Data for what test types exist for a subject with test transcripts
 *
 * This data is used to populate the subject and test filter options for TRT search.
 * The server returns a list of these for any given study.
 *
 * @param id data.account_subject.id database field
 * @param guid data.account_subject.reporting_id database field
 * @param testIds list of data.test.id ids
 */
case class AccountSubjectTests
(
	id: Long,
	guid: Guid,
	testIds: Seq[Long]
)

/** List of subjects and their test types */
class AccountSubjectTestData
(
	val subjects: Seq[AccountSubjectTests]
)
{
	/** Generates subject choices given the list of subject test data
	 *
	 * @param test Optional test filter
	 * @return subject choices
	 */
	def subjectChoices(test: Option[TestChoice]): Seq[AccountSubjectChoice] = {
		val filtered = test match {
			case None => subjects
			case Some(test) => subjects.filter(_.testIds.contains(test.testId))
		}
		filtered.map(s => AccountSubjectChoice(accountSubjectId = s.id, reportingId = s.guid)).sortBy(_.reportingId.noBracketsString)
	}

	/** Generates test choices given the list of subject test data
	 *
	 * @param subject Optional subject filter
	 * @param tests List of all tests
	 * @return test choices
	 */
	def testChoices(subject: Option[AccountSubjectChoice], tests: Seq[TestData]): Seq[TestChoice] = {
		val filtered = subject match {
			case Some(s) => subjects.find(_.id == s.accountSubjectId).map(Seq(_)).getOrElse(Seq[AccountSubjectTests]())
			case None => subjects
		}
		val ids = mutable.Set[Long]()
		for (s <- filtered; id <- s.testIds) {
			ids += id
		}
		val mapped = ids.map { id =>
			val test = tests.find(_.id == id).get
			TestChoice(testId = test.id, testName = test.displayName, testIdentifier = test.identifier)
		}
		mapped.toArray[TestChoice].sortBy(_.testName)
	}
}

object AccountSubjectTestData {
	/** Generates a list of subjects given a list of transcript-reviews
	 *
	 * @param transcripts All transcipt-reviews
	 * @return List of subjects
	 */
	def apply(transcripts: Seq[TranscriptReviewInstance]): AccountSubjectTestData = {
		val res = transcripts.groupBy(_.accountSubjectId) map { item =>
			val head = item._2.head
			AccountSubjectTests(id = head.accountSubjectId, guid = head.reportingId, testIds = item._2.map(_.testId).distinct)
		}
		new AccountSubjectTestData(res.toArray[AccountSubjectTests])
	}
}